/**
 * Singleton fetch client (Module 18 § C.4).
 *
 * Centralises everything the UI must NEVER duplicate:
 *   - Bearer token from authStore
 *   - X-Department-Id from authStore (auto-injected for dept-scoped paths)
 *   - 401 → forced logout + redirect
 *   - Consistent JSON parsing + error shape
 *   - SSE streaming via async iterator (chat.stream)
 *
 * Why a custom client and not Axios:
 *   The native fetch + ReadableStream APIs are exactly what we need for SSE,
 *   and adding Axios would require a separate adapter for SSE anyway.  This
 *   keeps the dependency surface small and the streaming code first-class.
 */

import { useAuthStore } from "../stores/authStore";

/** Path prefixes that must carry the X-Department-Id header. */
const DEPT_SCOPED_PREFIXES = [
  "/api/sadt/v2",
  "/api/calculate",
  "/api/excel",
  "/api/chat/v2",
  "/api/audit",
];

function isDeptScoped(path) {
  return DEPT_SCOPED_PREFIXES.some((p) => path.startsWith(p));
}

/**
 * Build the request headers for the active session.
 * Always reads from the auth store so a token rotation between calls is honoured.
 */
function buildHeaders(path, extra = {}) {
  const { token, activeDepartmentId } = useAuthStore.getState();
  const headers = {
    Accept: "application/json",
    ...extra,
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (isDeptScoped(path) && activeDepartmentId != null) {
    headers["X-Department-Id"] = String(activeDepartmentId);
  }
  return headers;
}

/**
 * Parse a JSON body, gracefully handling empty / non-JSON responses.
 * The backend's global error handler returns
 *   { success: false, error: "...", request_id: "..." }
 * so callers can rely on data.error existing on a failure.
 */
async function parseJson(res) {
  const text = await res.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { success: false, error: text };
  }
}

/**
 * Single 401 handler — forces logout and redirect.
 * Refresh-token retry slot is reserved for the cutover phase (Module 19).
 */
function handleUnauthorized() {
  const { logout } = useAuthStore.getState();
  logout();
  // Use location.replace so the user can't navigate "back" into the dead session.
  if (typeof window !== "undefined" && window.location.pathname !== "/auth/login") {
    window.location.replace("/auth/login");
  }
}

/**
 * Core fetch wrapper.
 * Throws an Error whose .data carries the parsed body so callers can render
 * field-level error messages from the backend.
 */
async function request(path, { method = "GET", body, headers, signal } = {}) {
  const init = {
    method,
    headers: buildHeaders(path, headers),
    signal,
  };

  // Send JSON for objects; pass FormData / Blob untouched so the browser
  // sets the right multipart boundary itself.
  if (body !== undefined && body !== null) {
    if (body instanceof FormData || body instanceof Blob) {
      init.body = body;
    } else {
      init.headers["Content-Type"] = "application/json";
      init.body = JSON.stringify(body);
    }
  }

  const res = await fetch(path, init);

  if (res.status === 401) {
    handleUnauthorized();
    const data = await parseJson(res);
    const err = new Error(data.error || "Unauthorized");
    err.status = 401;
    err.data = data;
    throw err;
  }

  const data = await parseJson(res);

  if (!res.ok) {
    const err = new Error(data.error || `Request failed: ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return data;
}

/**
 * SSE stream consumer.
 * Returns an async iterator that yields parsed frame objects, e.g.:
 *   for await (const frame of api.stream("/api/chat/v2/stream", { query })) { ... }
 *
 * Parses the standard event-stream framing produced by Flask:
 *   data: {json}\n\n
 *
 * The backend's frame types are: init | chunk | done | error.
 * Iteration ends naturally when the server closes the connection or sends "done".
 */
async function* stream(path, body) {
  const res = await fetch(path, {
    method: "POST",
    headers: {
      ...buildHeaders(path, { Accept: "text/event-stream" }),
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  if (res.status === 401) {
    handleUnauthorized();
    throw new Error("Unauthorized");
  }
  if (!res.ok) {
    const data = await parseJson(res);
    throw new Error(data.error || `Stream failed: ${res.status}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    // Frames are terminated by \n\n.  Pop them one at a time.
    let boundary;
    while ((boundary = buffer.indexOf("\n\n")) !== -1) {
      const event = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);

      if (!event.startsWith("data: ")) continue;
      const payload = event.slice(6);
      try {
        const frame = JSON.parse(payload);
        yield frame;
        if (frame.type === "done" || frame.type === "error") return;
      } catch {
        // Ignore malformed frames; the next \n\n will resync the parser.
      }
    }
  }
}

/**
 * Fetch a binary resource (xlsx, pdf…) with auth headers.
 * Returns { blob, filename } — filename is parsed from Content-Disposition.
 * Callers create an object URL and trigger a programmatic <a> click.
 */
async function blobRequest(path) {
  const res = await fetch(path, { method: "GET", headers: buildHeaders(path) });

  if (res.status === 401) {
    handleUnauthorized();
    throw new Error("Unauthorized");
  }
  if (!res.ok) {
    const data = await parseJson(res);
    throw new Error(data.error || `Request failed: ${res.status}`);
  }

  const blob = await res.blob();
  const disposition = res.headers.get("Content-Disposition") || "";
  const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
  const filename = match
    ? match[1].replace(/['"]/g, "")
    : `${path.split("/").pop()}.xlsx`;

  return { blob, filename };
}

export const api = {
  get:    (path)               => request(path, { method: "GET" }),
  post:   (path, body)         => request(path, { method: "POST", body }),
  patch:  (path, body)         => request(path, { method: "PATCH", body }),
  del:    (path)               => request(path, { method: "DELETE" }),
  blob:   blobRequest,
  stream,
};
